import React, { useEffect, useState } from "react";
import { FaEdit, FaImage, FaTimes } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getBannersAPI, addBannerAPI, updateBannerAPI, deleteBannerAPI } from '../../utils/api';
import { BASE_URL } from '../../utils/configConstants';
import Sidebar from "../Sidebar/Sidebar";
import AddBannerModal from "./components/AddBannerModal";
import "./Banner.css";

const Banner = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isNewImage, setIsNewImage] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [bannerUrl, setBannerUrl] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [selectedBannerForPreview, setSelectedBannerForPreview] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };



  const handleBannerClick = (banner) => {
    setSelectedBannerForPreview(banner);
    setImagePreviewModal(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewModal(false);
    setSelectedBannerForPreview(null);
  };

  // Convert image to base64
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Fetch banners from API
  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getBannersAPI();

      if (response?.status === 200) {
        setBanners(response?.data || []);
      } else if (response?.message) {
        setError(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error');
        setBanners([]);
      } else {
        setError('Failed due to internal error');
        setBanners([]);
      }
    } catch (err) {
      setError('Failed due to internal error');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(file?.type)) {
        showToast("Please select only JPG, PNG, JPEG, or WebP files", "error");
        return;
      }

      if (file && file?.type?.startsWith("image/")) {
        setSelectedImage(URL?.createObjectURL(file));
        setIsNewImage(true);
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(file?.type)) {
        showToast("Please select only JPG, PNG, JPEG, or WebP files", "error");
        return;
      }

      if (file?.type?.startsWith("image/")) {
        setSelectedImage(URL?.createObjectURL(file));
        setIsNewImage(true);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };



  const handleEdit = (banner) => {
    setSelectedBanner(banner);
    setSelectedImage(BASE_URL + banner?.image);
    setBannerUrl(banner?.url || '');
    setIsNewImage(false);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setSelectedBanner(null);
    setSelectedImage(null);
    setBannerUrl('');
    setIsNewImage(false);
    setIsEditMode(false);
    setShowModal(true);
    setSaveLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage(null);
    setBannerUrl('');
    setIsNewImage(false);
    setIsEditMode(false);
    setSelectedBanner(null);
  };

  const handleDeleteClick = (banner) => {
    setBannerToDelete(banner);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBannerToDelete(null);
  };



  const handleDeleteConfirm = async () => {
    try {
      if (!bannerToDelete?._id) {
        showToast("Invalid banner data", "error");
        return;
      }

      const reqData = {
        banner_id: bannerToDelete._id
      };

      const response = await deleteBannerAPI(reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setBannerToDelete(null);
        showToast(`Banner deleted successfully!`, "success");
        await fetchBanners(); // Refresh the list
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleSave = async () => {
    if (!selectedImage) {
      showToast("Please select an image", "error");
      return;
    }
    setSaveLoading(true);

    try {
      // Get the file from the input
      const fileInput = document?.getElementById(isEditMode ? 'edit-banner-upload-input' : 'banner-upload-input');
      const file = fileInput?.files[0];

      if (!file && isNewImage) {
        showToast("Please select an image file", "error");
        return;
      }

      if (isEditMode && selectedBanner && !isNewImage&&!bannerUrl) {
        // No new image selected, just close modal
        handleCloseModal();
        return;
      }

      
     
      if (isEditMode) {
        // Update existing banner
        const reqData = {
          banner_id: selectedBanner?._id,
          url: bannerUrl
        };
        
        // Only include image if a new image is selected
        if (isNewImage) {
          // Convert image to base64
          const base64Image = await convertImageToBase64(file);
          reqData.image = base64Image;
        }

        const response = await updateBannerAPI(reqData);

        if (response?.status === 200) {
          await fetchBanners();
          handleCloseModal();
          showToast("Banner updated successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
      } else {
        // Add new banner
        const base64Image = await convertImageToBase64(file);
        const reqData = {
          image: base64Image,
          url: bannerUrl
        };

        const response = await addBannerAPI(reqData);

        if (response?.status === 200) {
          await fetchBanners();
          handleCloseModal();
          showToast("Banner added successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="Banner-layout">
        <Sidebar />
        <div className="Banner-main-content">
          <div className="Banner-header-row">
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Banner"
            >
              <span>Add Banner</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading banners...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : banners.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🖼️</div>
              <h3>No Banners Found</h3>
              <p>There are no banners in the system yet.</p>
              <p>Click "Add Banner" to create the first one.</p>
            </div>
          ) : (
            <div className="banner-grid">
              {banners.map((banner, idx) => (
                <div key={banner._id || `banner-${idx}`} className="banner-card">
                  <div className="banner-image-container">
                    <img
                      src={BASE_URL + banner.image}
                      alt={`Banner ${banner._id}`}
                      className="banner-img"
                      onClick={() => handleBannerClick(banner)}
                    />
                    <div className="banner-actions-overlay">
                      <button
                        type="button"
                        className="edit-btn"
                        title="Edit Banner"
                        onClick={() => handleEdit(banner)}
                        aria-label="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="delete-btn"
                        title="Delete Banner"
                        onClick={() => handleDeleteClick(banner)}
                        aria-label="Delete"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit Modal */}
          <AddBannerModal
            showModal={showModal}
            isEditMode={isEditMode}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            isNewImage={isNewImage}
            setIsNewImage={setIsNewImage}
            handleDrop={handleDrop}
            handleDragOver={handleDragOver}
            handleFileChange={handleFileChange}
            saveLoading={saveLoading}
            onSave={handleSave}
            onClose={handleCloseModal}
            bannerUrl={bannerUrl}
            setBannerUrl={setBannerUrl}
          />

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete this banner?`}
            confirmText="Delete"
          />

          {imagePreviewModal && selectedBannerForPreview && (
            <div className="modal-overlay" onClick={handleCloseImagePreview}>
              <div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Banner Preview</h3>
                  <button className="modal-close" onClick={handleCloseImagePreview}>
                    <FaTimes />
                  </button>
                </div>
                <div className="modal-body">
                  <img
                    src={BASE_URL + selectedBannerForPreview.image}
                    alt="Document Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '70vh',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Banner; 